import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface Props {
    name?: string;
    accept?: string;
    multiple?: boolean;
    value?: File | File[] | null | undefined;
    onChange: (file: File | File[] | null) => void;
    className?: string;
    buttonClassName?: string;
    buttonVariant?: string;
    chooseKey?: string; // translation key for button
    noFileKey?: string; // translation key when no file chosen
}

const LocalizedFileInput: React.FC<Props> = ({
    accept = "*",
    multiple = false,
    value,
    onChange,
    className,
    buttonClassName = "btn btn-light",
    chooseKey = "general.chooseFiles",
    noFileKey = "general.noFileChosen",
}) => {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [fileName, setFileName] = useState<string | null>(null);

    useEffect(() => {
        if (multiple) {
            if (Array.isArray(value) && value.length > 0) {
                setFileName(value.map((f) => f.name).join(", "));
            } else {
                setFileName(null);
            }
        } else {
            if (value && !(Array.isArray(value)) && value instanceof File) setFileName(value.name);
            else setFileName(null);
        }
    }, [value, multiple]);

    const handleClick = () => inputRef.current?.click();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (multiple) {
            const files = e.target.files ? Array.from(e.target.files) : [];
            setFileName(files.length ? files.map((f) => f.name).join(", ") : null);
            onChange(files.length ? files : null);
        } else {
            const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
            setFileName(f ? f.name : null);
            onChange(f);
        }
    };

    return (
        <div className={className}>
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                multiple={multiple}
                style={{ display: "none" }}
                onChange={handleChange}
            />
            <div className="d-flex align-items-center gap-3">
                <button type="button" className={buttonClassName} onClick={handleClick}>
                    {t(chooseKey)}
                </button>
                <div className="text-muted">{fileName ?? t(noFileKey)}</div>
            </div>
        </div>
    );
};

export default LocalizedFileInput;
